import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import Loader from '../../../components/Loader'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import MovingPrice from '../../../components/v2/MovingPrice'
import PromoCodesInput from '../../../components/v2/PromoCodesInput'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Accordion from '../../../components/v2/accordions/Accordion'
import Checkbox from '../../../components/v2/buttons/Checkbox'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import DialogModal from '../../../components/v2/modals/DialogModal'
import PetProgressbar from '../../../components/v2/progrees-bars/PetProgressBar'
import { currencySymbol, georgianFormatter } from '../../../domain/types'
import { RootState, useAppSelector } from '../../../store'
import { AppState } from '../../../store/appSlice'
import { AuthState } from '../../../store/authSlice'
import {
  PetState,
  setDiscountUsedSuccessfully,
  setDiscountedPayment,
  setInsurerInfo,
  setOfferTotalPrice,
  setPayment,
  setPromoCode,
  setPromoCodeUsedSuccessfully,
  setPromoCodeValue,
} from '../../../store/petSlice'

const PetCheckInformation = () => {
  //
  const dispatch = useDispatch()
  //
  const { t } = useTranslation()
  //
  const navigate = useNavigate()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  //
  const { checkout, filter, screenHeader } = useAppSelector((data: { pet: PetState }) => data.pet)
  //
  const [franiTersModalIsOpen, setFraniTersModalIsOpen] = useState<boolean>(false)
  //
  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)
  //
  const { data: productCategories, isLoading: isProductCategoryLoading } =
    api.useGetProductCategoriesListQuery(undefined)
  //
  const categoryId = productCategories?.find((x) => x.slug === 'pet')?.id || 0
  //
  const { data: informationsheetlist, isLoading: isInformationSheetListLoading } = api.useGetInformationSheetQuery({
    categoryId: categoryId,
    attachmentSlug: 'information-sheet',
  })
  //
  const itemID = informationsheetlist?.find((x) => x.productCategoryId === categoryId)?.id || 0
  //
  const { data: currentInformationSheet, isLoading: isCurrentInformationSheetLoading } =
    api.useGetInformationSheetDetailQuery(itemID, {
      skip: !itemID && true,
    })
  //
  const closeFraniTersModal = () => {
    setFraniTersModalIsOpen(false)
  }
  //
  const [inurerCompanuTermsModalIsOpen, setFnurerCompanuTermsModalIsOpen] = useState<boolean>(false)
  //
  const closeInsurerCompanyModal = () => {
    setFnurerCompanuTermsModalIsOpen(false)
  }
  //
  //
  const { data: pageInfo, isLoading: isPageInfoLoading } = api.useGetPetPageInfoQuery('pet-check-info')
  //
  const pageTitle = pageInfo?.translations[lang]?.title
  const stepName = pageInfo?.translations[lang]?.stepName
  //
  // ? Discount from promo code
  //
  const promoCodeQuery = `${filter.promoCode !== '' ? `PromoCode=${filter.promoCode}&` : ``}petPriceId=${
    filter?.offer?.petPriceId
  }&policyStartDate=${filter.dates?.start}&packageId=${filter.offer?.package.id}${
    auth?.user?.id ? `&userId=${auth?.user?.id}` : ``
  }`
  //
  //
  const {
    data: discountedPayments,
    isLoading: isDiscountedPaymentsLoading,
    error: discountedPaymentsError,
    isSuccess: discountSuccess,
  } = api.useGetPetPaymentDiscountQuery(
    {
      query: promoCodeQuery,
    },
    {
      skip: filter.promoCode && filter.offer?.petPriceId ? false : true,
      refetchOnMountOrArgChange: true,
    },
  )
  // ? Discount from promo code
  //
  const setPaymentInStore = useCallback(() => {
    // when promo code is used and discounted payment is returned in offer
    if (filter.offer && filter.discountedPayment !== undefined && filter.discountUsedSuccessfully) {
      dispatch(
        setPayment({
          firstPaymentAmount: filter.discountedPayment?.firstPaymentAmount?.discounted,
          monthlyPaymentAmount: filter.discountedPayment?.monthlyPaymentAmount?.discounted,
          totalPaymentAmount: filter.discountedPayment?.totalPaymentAmount?.discounted,
        }),
      )
    }
    // when promo code isn't used and discounted payment is null in offer
    if (filter?.offer && !filter.discountUsedSuccessfully) {
      dispatch(
        setPayment({
          firstPaymentAmount: filter.offer.firstPaymentAmount,
          monthlyPaymentAmount: filter.offer.monthlyPaymentAmount,
          totalPaymentAmount: filter.offer.totalPaymentAmount,
        }),
      )
      //
    }
  }, [dispatch, filter.offer, filter.discountedPayment, filter.discountUsedSuccessfully])
  //
  const setDiscountInStore = useCallback(() => {
    //
    if (!discountedPaymentsError && filter?.promoCode !== '') {
      return [
        dispatch(setDiscountUsedSuccessfully(true)),
        dispatch(setPromoCodeUsedSuccessfully(true)),
        dispatch(setDiscountedPayment(discountedPayments)),
      ]
      //
    }

    if (discountedPaymentsError !== undefined && filter.promoCode !== '') {
      //
      return [
        dispatch(setDiscountedPayment(undefined)),
        dispatch(setDiscountUsedSuccessfully(false)),
        dispatch(setPromoCodeUsedSuccessfully(false)),
        toast.error(`${t('promoCodeCantbeUsed')}`, { toastId: 1 }),
      ]
      //
    }
    //
  }, [t, dispatch, discountedPayments, filter?.promoCode, discountedPaymentsError])
  //
  useEffect(() => {
    //
    setPaymentInStore()
    //
    return () => {}
  }, [setPaymentInStore])
  //
  useEffect(() => {
    //
    setDiscountInStore()
    //
    return () => {}
  }, [setDiscountInStore])
  //
  const setPromoCodeInStore = useCallback(() => {
    // if discount isn't used successfully, promo code must be empty string, so error will be showned only once
    if (!filter.discountUsedSuccessfully) {
      dispatch(setPromoCode(''))
    }
    //
  }, [filter.discountUsedSuccessfully, dispatch])
  //
  useEffect(() => {
    setPromoCodeInStore()
    //
    return () => {}
  }, [setPromoCodeInStore])
  //
  const _Price =
    filter.offer && filter?.payment
      ? filter?.offer?.paymentScheduleType?.slug === 'ONE-TIME'
        ? georgianFormatter.format(filter.payment?.totalPaymentAmount as number)
        : georgianFormatter.format(filter.payment?.firstPaymentAmount as number)
      : '0'
  //
  const _DiscountValue =
    filter?.offer && filter?.payment && filter.discountedPayment && filter.discountUsedSuccessfully
      ? filter.discountedPayment?.totalPaymentAmount?.saving
      : null
  //
  useEffect(() => {
    if (_Price) {
      dispatch(setOfferTotalPrice(_Price?.toString()))
    }
    //
    return () => {}
  }, [_Price, dispatch])
  //
  //
  const CheckboxText = `${t('iReadAndAgree')} ${filter?.offer?.translations?.[lang]?.title} ${
    lang === 'ge' ? ' - ს' : "'s"
  } ${t('insuranceCondition')}`
  //
  const parsedPaymentScheduleType =
    typeof filter?.offer?.paymentScheduleType?.translations === 'string'
      ? JSON.parse(filter?.offer?.paymentScheduleType?.translations)
      : filter?.offer?.paymentScheduleType?.translations
  //
  //
  // auto navigate first page
  useEffect(() => {
    if (filter?.petCategoryId === null) {
      navigate('/choose-pet')
    }
    //
    return () => {}
  }, [filter?.petCategoryId, navigate])
  //
  const MainLoading =
    isPageInfoLoading ||
    isCountriesLoading ||
    isProductCategoryLoading ||
    isInformationSheetListLoading ||
    isCurrentInformationSheetLoading ||
    isDiscountedPaymentsLoading
  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageTitle} />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      {/* frani terms modal */}
      <DialogModal
        title={`${t('franiConditions')}`}
        isOpen={franiTersModalIsOpen}
        onClose={closeFraniTersModal}
        src={currentInformationSheet?.attachment?.[lang]?.url || ''}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'privacyTerm',
              fieldValue: true,
            }),
          ),
          closeFraniTersModal(),
        ]}
        //
        declineOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'privacyTerm',
              fieldValue: false,
            }),
          ),
          closeFraniTersModal(),
        ]}
      />
      {/* frani terms modal */}

      {/* insurance company terms */}
      <DialogModal
        title={CheckboxText}
        isOpen={inurerCompanuTermsModalIsOpen}
        onClose={closeInsurerCompanyModal}
        src={filter.offer?.termsPath?.translations?.[lang]?.url || ''}
        isPdf={true}
        acceptOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'policyTerm',
              fieldValue: true,
            }),
          ),
          closeInsurerCompanyModal(),
        ]}
        //
        declineOnClick={() => [
          dispatch(
            setInsurerInfo({
              fieldName: 'policyTerm',
              fieldValue: false,
            }),
          ),
          closeInsurerCompanyModal(),
        ]}
      />
      {/* insurance company terms */}

      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />

      <MovingPrice price={filter.offerTotalPrice} currency="GEL" />

      <Container>
        <Content>
          <Title text={pageTitle} />

          <PetProgressbar stepName={stepName} />

          <Block>
            <Accordion
              isOpen={false}
              editBtnOnchange={() => navigate('/pet-info')}
              editBtnText={t('edit')}
              title={t('petInfo')}
            >
              <TextDiv>
                <LeftText>{t('age')}</LeftText>
                <RightText>
                  {filter?.petInfo?.age ? `${filter.petInfo.age} ${t('year')}` : null}{' '}
                  {filter?.petInfo?.month ? `${filter.petInfo.month} ${t('month')}` : null}
                </RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('color')}</LeftText>
                <RightText>{filter.petInfo.color}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('breed')}</LeftText>
                <RightText>
                  {filter?.petInfo?.breed === 'other' || filter?.petInfo?.breed === 'სხვა'
                    ? filter.petInfo.breedOther
                    : filter.petInfo.breed}
                </RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('gender')}</LeftText>
                <RightText>{filter.petInfo.gender === 'female' ? t('petFemale') : t('petMale')}</RightText>
              </TextDiv>
            </Accordion>
          </Block>

          <Block>
            <Accordion
              isOpen={false}
              editBtnOnchange={() => navigate('/pet-insurer')}
              editBtnText={t('edit')}
              title={`${t('yourPersonalInfo')}`}
            >
              <TextDiv>
                <LeftText>{t('cityzenship')}</LeftText>
                <RightText>
                  {countries?.find((x) => x.isoCode === checkout.insurer?.cityzenship)?.translations?.[lang]?.name}
                </RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('personalNumber')}</LeftText>
                <RightText>{checkout.insurer?.idNumber}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('dateOfBirth')}</LeftText>
                <RightText>{checkout.insurer?.dateOfbirth?.toString()}</RightText>
              </TextDiv>

              {checkout.insurer?.translations?.ge?.firstName && (
                <TextDiv>
                  <LeftText>{t('nameInGeorgia')}</LeftText>
                  <RightText>{checkout.insurer?.translations?.ge?.firstName}</RightText>
                </TextDiv>
              )}

              {checkout.insurer?.translations?.ge?.lastName && (
                <TextDiv>
                  <LeftText>{t('surnameInGeorgia')}</LeftText>
                  <RightText>{checkout.insurer?.translations?.ge?.lastName}</RightText>
                </TextDiv>
              )}

              <TextDiv>
                <LeftText>{t('nameInEnglish')}</LeftText>
                <RightText>{checkout.insurer?.translations?.en?.firstName}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('surnameInEnglish')}</LeftText>
                <RightText>{checkout.insurer?.translations?.en?.lastName}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('gender')}</LeftText>
                <RightText>
                  {checkout.insurer.gender === 'female' && `${t('female')}`}
                  {checkout.insurer.gender === 'male' && `${t('male')}`}
                </RightText>
              </TextDiv>
            </Accordion>
          </Block>

          <Block>
            <Accordion
              isOpen={true}
              editBtnOnchange={() => alert('edit btn')}
              editBtnText={`${t('edit')}`}
              hideEditBtn={true}
              title={`${t('policyInfo')}`}
              disableEditBtn={true}
            >
              <TextDiv>
                <LeftTextForCompany>{t('insuranceCompany')}</LeftTextForCompany>
                <RightText>{filter?.offer?.translations[lang]?.title}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('category')}</LeftText>
                <RightText>{filter?.offer?.policyType === 'PetPolicy' ? t('petInsurance') : ''}</RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('policyDuration')}</LeftText>
                <RightText>
                  {filter?.offer?.days} {t('day')}
                </RightText>
              </TextDiv>

              <TextDiv>
                <LeftText>{t('startDate')}</LeftText>
                <RightText>{dayjs(filter.dates?.start).format('DD  MMMM  YYYY')}</RightText>
              </TextDiv>

              <hr />
              <PromoCodesInput
                value={filter?.promoCodeValue || ''}
                onChange={(value) => dispatch(setPromoCodeValue(value))}
                onClick={() => {
                  if (filter.promoCodeUsedSuccessfully === false) {
                    //
                    dispatch(setPromoCode(filter?.promoCodeValue))
                    //
                    //
                  } else {
                    dispatch(setPromoCode(''))
                    //
                    dispatch(setPromoCodeValue(''))
                    //
                    dispatch(setPayment(undefined))
                    //
                    dispatch(setDiscountUsedSuccessfully(false))
                    //
                    dispatch(setPromoCodeUsedSuccessfully(false))
                    //
                    dispatch(setDiscountedPayment(undefined))
                  }
                  //
                }}
                buttonSize="medium"
                discountValue={_DiscountValue}
                disabled={filter?.promoCode !== '' ? filter.discountUsedSuccessfully : false}
                discountUsedSuccesfully={filter?.promoCode !== '' ? filter.discountUsedSuccessfully : false}
                currency={'gel'}
              />

              <hr />
              <TextDiv>
                <LeftText>{t('paymentSchedule')}</LeftText>
                <RightText>{parsedPaymentScheduleType && parsedPaymentScheduleType[lang]?.title}</RightText>
              </TextDiv>

              {filter?.offer?.paymentScheduleType?.slug === 'ONE-TIME' ? null : (
                <TextDiv>
                  <LeftText>{t('annualPaymentTitle')}</LeftText>
                  <RightText>
                    {filter.discountedPayment && filter.discountUsedSuccessfully && (
                      <DiscountInfo>{filter.discountedPayment?.totalPaymentAmount?.actual} ₾</DiscountInfo>
                    )}
                    {filter?.payment?.totalPaymentAmount} ₾
                  </RightText>
                </TextDiv>
              )}

              <TextDiv style={{ marginTop: '6px' }}>
                <LeftText style={{ color: `${Colors.primaryBlack}` }}>{`${t('currentPayment')}`}</LeftText>{' '}
                <PriceInfo>
                  {filter.discountedPayment && filter.discountUsedSuccessfully && (
                    <DiscountInfo>
                      {filter.offer?.paymentScheduleType.slug === 'ONE-TIME'
                        ? filter?.offer?.totalPaymentAmount
                        : filter?.offer?.firstPaymentAmount}{' '}
                      {currencySymbol('GEL')}
                    </DiscountInfo>
                  )}

                  <PriceText>
                    {georgianFormatter.format(Number(_Price) || 0)} {currencySymbol('GEL')}
                  </PriceText>
                </PriceInfo>
              </TextDiv>

              <hr />

              <Checkbox
                id="tpl-check-information-privacy-terms"
                label={`${t('franiConditions')}`}
                checked={checkout.insurer.privacyTerm || false}
                defaultChecked={checkout.insurer.privacyTerm || false}
                onClick={() => setFraniTersModalIsOpen(true)}
              />

              <hr />

              <Checkbox
                id="tpl-check-information-company-terms"
                label={CheckboxText}
                checked={checkout.insurer.policyTerm || false}
                defaultChecked={checkout.insurer.policyTerm || false}
                onClick={() => setFnurerCompanuTermsModalIsOpen(true)}
              />
            </Accordion>
          </Block>

          <Buttons>
            <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/pet-insurer')} />
            <NavigationButton
              text={`${t('next')}`}
              buttonType="next"
              disabled={checkout.insurer.policyTerm === true && checkout.insurer.privacyTerm === true ? false : true}
              onClick={() => {
                if (
                  filter &&
                  filter.promoCodeValue &&
                  filter.promoCodeValue.length >= 2 &&
                  !discountedPaymentsError &&
                  !discountSuccess
                ) {
                  toast.info(`${t('pleaseActivatePromoCode')}`)
                } else {
                  navigate('/pet-policy-registration')
                }
              }}
            />
          </Buttons>
        </Content>
      </Container>
      <BackgroundIcon />
    </Wrapper>
  )
}
//
//
const PriceText = styled.span`
  font-size: 18px;
  color: ${Colors.primaryBlack};
  text-decoration: none;
  font-weight: 500;
`
const PriceInfo = styled.span`
  color: ${Colors.primaryBlack};
`
//
const DiscountInfo = styled.span`
  font-size: 14px;
  text-decoration: line-through;
  color: ${Colors.nevadaGrey};
  margin-right: 12px;
`
const RightText = styled.div`
  text-align: right;
`
const LeftText = styled.div`
  width: 60%;
`
const LeftTextForCompany = styled.div``
const TextDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${Colors.nevadaGrey};
  font-size: 14px;
  margin-bottom: 14px;
`
const Block = styled.div`
  max-width: 464px;
  width: 100%;
  margin-inline: auto;
  margin-top: 32px;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`
const Content = styled.div`
  padding-block: 60px;
  position: sticky;
  z-index: 1;

  ${Responsive.mobile} {
    padding-block: 30px;
  }
`
const Wrapper = styled.div`
  /*  */

  .birtyday-input {
    font-weight: normal !important;
  }
`
//
export default PetCheckInformation
